import { useState } from 'react'
import { useStockAudio } from 'hooks/queries/designs/useStockAudio'
import { Spinner } from 'common/Spinner/Spinner'
import { Button } from 'blaze-ui/Button'
import { AudioItem } from './AudioItem'
import { AudioUploadTile } from './AudioUploadTile'
import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './AudioEditorModal.module.scss'
import { ChevronDown, Search } from 'blaze-ui/icons'
import { useDebounce } from 'hooks/useDebounce'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useInfiniteBrandKitMediaFiles } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { collectResults } from 'utils/query'
import { ACCEPTED_AUDIO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'

import type { MediaObject, AudioAsset, AlmanacFile } from 'types'

interface AudioLibraryStepProps {
  onSelectAudio: (audio: AudioAsset) => void
  mediaObject: MediaObject
}

const convertBrandKitFilesToAudioItems = (files: AlmanacFile[]): AudioAsset[] => {
  return files.map(
    (file) =>
      ({
        id: file.id.toString(),
        title: file.title || 'Untitled Audio',
        duration: file.duration || 0,
        preview_url: file.source_url,
        type: 'brand_kit',
      } as AudioAsset),
  )
}

export const AudioLibraryStep = ({ onSelectAudio, mediaObject }: AudioLibraryStepProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [playingAudioId, setPlayingAudioId] = useState<AudioAsset['id'] | null>(null)
  const { data: brandKits } = useBrandKits()
  const brandKitId = brandKits?.[0]?.id
  const debouncedQuery = useDebounce(searchQuery, 500)

  const brandKitMediaFilesQuery = useInfiniteBrandKitMediaFiles({
    brandKitId: brandKitId || 0,
    items: 4,
    order_by: 'recent',
    mime_types: ACCEPTED_AUDIO_MIME_TYPES,
  })

  const brandKitMediaFiles = collectResults(brandKitMediaFilesQuery, (data) => data.media_files)
  const brandKitAudioItems = convertBrandKitFilesToAudioItems(brandKitMediaFiles as AlmanacFile[])

  const stockAudioQuery = useStockAudio({
    query: debouncedQuery,
    perPage: 20,
    minDuration: mediaObject.duration,
  })

  const stockAudioItems = stockAudioQuery.data?.pages?.flatMap((page) => page.items) || []

  const handleNewAudio = (newAudio: AlmanacFile) => {
    const audioAsset = convertBrandKitFilesToAudioItems([newAudio])[0]
    onSelectAudio(audioAsset)
  }

  return (
    <div className={Styles.libraryStep}>
      {brandKitId && (
        <div className={Styles.section}>
          <Heading level={4}>From Brand Kit</Heading>

          {brandKitMediaFilesQuery.isLoading && (
            <div className={Styles.loadingContainer}>
              <Spinner size="large" />
            </div>
          )}

          {!brandKitMediaFilesQuery.isLoading && (
            <>
              <div className={Styles.audioGrid}>
                <AudioUploadTile brandKitId={brandKitId} onSuccess={handleNewAudio} />
                {brandKitAudioItems.map((audio, index) => (
                  <AudioItem
                    key={`${audio.id}-${index}`}
                    audio={audio}
                    onSelect={() => onSelectAudio(audio)}
                    playingAudioId={playingAudioId}
                    onPlayingChange={setPlayingAudioId}
                  />
                ))}
              </div>

              {brandKitMediaFilesQuery.isFetchingNextPage && (
                <div className={Styles.loadingContainer}>
                  <Spinner size="large" />
                </div>
              )}

              {brandKitMediaFilesQuery.hasNextPage && !brandKitMediaFilesQuery.isFetchingNextPage && (
                <Button
                  variant="tertiary"
                  className={Styles.showMoreButton}
                  endIcon={ChevronDown}
                  size="sm"
                  onPress={() => brandKitMediaFilesQuery.fetchNextPage()}
                  isDisabled={brandKitMediaFilesQuery.isFetchingNextPage}
                >
                  Show More
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <div className={Styles.section}>
        <Heading level={4}>Stock audio</Heading>

        <div className={Styles.searchField}>
          <Search />
          <Input
            className={Styles.searchInput}
            name="search"
            id="audioSearch"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            placeholder="Search for audio..."
            autoComplete="off"
            aria-label="Search for audio..."
            withBorder={false}
          />
        </div>

        {stockAudioQuery.isLoading && (
          <div className={Styles.loadingContainer}>
            <Spinner size="large" />
          </div>
        )}

        {!stockAudioQuery.isLoading && stockAudioItems.length === 0 && (
          <div className={Styles.emptyState}>No audio found. Try a different search term.</div>
        )}

        {!stockAudioQuery.isLoading && stockAudioItems.length > 0 && (
          <>
            <div className={Styles.audioGrid}>
              {stockAudioItems.map((audio, index) => (
                <AudioItem
                  key={`${audio.id}-${index}`}
                  audio={audio}
                  onSelect={() => onSelectAudio(audio)}
                  playingAudioId={playingAudioId}
                  onPlayingChange={setPlayingAudioId}
                />
              ))}
            </div>

            {stockAudioQuery.isFetchingNextPage && (
              <div className={Styles.loadingContainer}>
                <Spinner size="large" />
              </div>
            )}

            {stockAudioQuery.hasNextPage && !stockAudioQuery.isFetchingNextPage && (
              <Button
                variant="tertiary"
                className={Styles.showMoreButton}
                endIcon={ChevronDown}
                size="sm"
                onPress={() => stockAudioQuery.fetchNextPage()}
                isDisabled={stockAudioQuery.isFetchingNextPage}
              >
                Show More
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
